import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type {
	AcceptInvitationResponse,
	InvitationContextResponse,
} from "@zplanem/responses"
import { webPaths } from "@zplanem/web-paths"
import { useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { ApiError, apiFetch, apiUrl } from "../../lib/api-client"
import { queryKeys } from "../../lib/query-keys"
import { useAuth } from "../auth-context"
import { getStoredLocale, getTranslations } from "../i18n"
import { useLocale } from "../locale-context"

async function fetchInvitationContext(
	token: string,
): Promise<InvitationContextResponse> {
	// Public-by-token endpoint: anyone holding the unguessable token can read
	// property name, role, inviter name, expiry. Use plain `fetch` so the
	// request goes through even when no Firebase user is signed in (apiFetch
	// refuses in that case). Re-throw as `ApiError` so the status-code
	// branching in the page treats both fetch paths uniformly.
	//
	// Route through `apiUrl(...)` so deployed builds (where web and api live
	// on different origins) prepend the VITE_API_BASE_URL set at build time;
	// in dev the helper is a no-op and the vite proxy handles the relative
	// path. A bare `/api/...` here lands on the SPA host, which returns
	// index.html and the JSON parse below explodes with the classic
	// "Unexpected token '<'" error.
	const url = apiUrl(`/api/common/invitations/${encodeURIComponent(token)}`)
	const res = await fetch(url, {
		headers: {
			"Content-Type": "application/json",
			"Accept-Language": getStoredLocale(),
		},
	})
	const isJson = res.headers.get("content-type")?.includes("application/json")
	if (res.ok) {
		// Guard the parse: a 200 with non-JSON content-type means the request
		// was misrouted (e.g. SPA fallback), not that the API actually
		// answered. Surface a clear error instead of a JSON SyntaxError.
		if (!isJson) {
			throw new ApiError(
				502,
				"Invitation API returned non-JSON response (request likely misrouted to the SPA host)",
			)
		}
		return (await res.json()) as InvitationContextResponse
	}
	const body = isJson ? await res.json().catch(() => null) : null
	const message =
		(body &&
		typeof body === "object" &&
		"message" in body &&
		typeof (body as { message: unknown }).message === "string"
			? (body as { message: string }).message
			: null) ??
		res.statusText ??
		"Request failed"
	throw new ApiError(res.status, message)
}

/**
 * Public landing page for invitation magic links (`/invitations/<token>`).
 *
 * The GET context endpoint is public-by-token; the POST accept endpoint is
 * authenticated (`authenticateUser`, NOT `authenticateOnboardedUser`), gated
 * behind the signed-in branch in the UI.
 */
export function InvitationAcceptPage() {
	const { token } = useParams<{ token: string }>()
	const { user, loading: authLoading, logout } = useAuth()
	const { locale } = useLocale()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const t = getTranslations(locale)
	const tt = t.acceptInvitation

	const [accepted, setAccepted] = useState<
		AcceptInvitationResponse["property"] | null
	>(null)

	const contextQuery = useQuery({
		queryKey: ["invitation-context", token],
		queryFn: () => fetchInvitationContext(token as string),
		enabled: !!token,
		retry: false,
	})

	const acceptMutation = useMutation({
		mutationFn: () =>
			apiFetch<AcceptInvitationResponse>(
				`/api/common/invitations/${encodeURIComponent(token as string)}/accept`,
				{ method: "POST" },
			),
		onSuccess: async (res) => {
			setAccepted(res.property)
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: queryKeys.properties() }),
				// Refresh the property journal so the "invitation accepted"
				// activity event (AC10) is visible on the journal tab the
				// next time the invitee navigates there. The accepted
				// property's id only becomes known here (response payload),
				// hence the direct queryClient call instead of the
				// `useJournalCacheInvalidator` hook which expects a stable
				// propertyId at construction time.
				queryClient.invalidateQueries({
					queryKey: queryKeys.propertyJournal(res.property.id),
				}),
			])
		},
	})

	const invitationEmail = contextQuery.data?.invitation.email ?? ""

	function translateError(err: unknown): string {
		if (!(err instanceof ApiError)) {
			return err instanceof Error ? err.message : "Unknown error"
		}
		if (err.status === 410) return tt.terminalErrors.expired
		if (err.status === 404) {
			// API returns 404 for both "unknown token" and "already accepted".
			// Sniff the message to pick the right copy.
			return err.message.toLowerCase().includes("accepted")
				? tt.terminalErrors.alreadyAccepted
				: tt.terminalErrors.notFound
		}
		if (err.status === 403) {
			return tt.emailMismatch.replace("{invitationEmail}", invitationEmail)
		}
		return err.message
	}

	if (!token) {
		return (
			<div className="min-h-screen flex flex-col items-center justify-center bg-background px-6 text-center">
				<p role="alert" className="text-base text-destructive max-w-md">
					{tt.terminalErrors.notFound}
				</p>
				<div className="mt-6">
					<Button
						variant="brand"
						size="lg"
						onClick={() => navigate(webPaths.dashboard())}
					>
						{tt.goToApp}
					</Button>
				</div>
			</div>
		)
	}

	if (authLoading || contextQuery.isLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">
				{tt.loadingState}
			</div>
		)
	}

	if (accepted) {
		return (
			<div className="min-h-screen flex flex-col items-center justify-center bg-background px-6 text-center">
				<h1 className="text-3xl font-bold text-foreground">
					{tt.success.replace("{propertyName}", accepted.name)}
				</h1>
				<div className="mt-6">
					<Button
						variant="brand"
						size="lg"
						onClick={() => navigate(webPaths.dashboard())}
					>
						{tt.backToDashboard}
					</Button>
				</div>
			</div>
		)
	}

	const displayedError = acceptMutation.error ?? contextQuery.error
	if (displayedError) {
		return (
			<div className="min-h-screen flex flex-col items-center justify-center bg-background px-6 text-center">
				<p role="alert" className="text-base text-destructive max-w-md">
					{translateError(displayedError)}
				</p>
				<div className="mt-6 flex flex-col items-center gap-3">
					<Button
						variant="brand"
						size="lg"
						onClick={() => navigate(webPaths.dashboard())}
					>
						{tt.goToApp}
					</Button>
					{user ? (
						// Useful escape hatch when the message is an email-mismatch and
						// the user needs to sign back in with a different account.
						<Button variant="ghost" onClick={() => void logout()}>
							{t.sidebar.signOut}
						</Button>
					) : null}
				</div>
			</div>
		)
	}

	if (!contextQuery.data) return null

	const inv = contextQuery.data.invitation
	const property = contextQuery.data.property
	const inviter = contextQuery.data.inviter
	const inviterName = (() => {
		const name = [inviter.firstName, inviter.lastName]
			.filter(Boolean)
			.join(" ")
			.trim()
		// Fallback to the invitee's email when the inviter row has no display
		// name. QR-kind invitations have null email, so fall further back to a
		// placeholder.
		return name.length > 0 ? name : (inv.email ?? tt.unknownInviter)
	})()

	// QR-kind invitations can't be accepted on the web — the security model is
	// "QR + PIN entered on the mobile app".
	if (inv.kind === "qr") {
		return (
			<div className="min-h-screen flex flex-col items-center justify-center bg-background px-6 py-16">
				<div className="w-full max-w-lg rounded-lg border border-border bg-muted/40 p-10 text-center">
					<h1 className="text-2xl font-bold text-foreground">
						{tt.qrOpenInAppTitle.replace("{propertyName}", property.name)}
					</h1>
					<p className="mt-4 text-sm text-foreground/80">
						{tt.qrOpenInAppBody}
					</p>
				</div>
			</div>
		)
	}

	const userEmail = user?.email ?? null
	const emailMatches =
		userEmail !== null &&
		userEmail.toLowerCase() === invitationEmail.toLowerCase()

	return (
		<div className="min-h-screen flex flex-col items-center justify-center bg-background px-6 py-16">
			<div className="w-full max-w-lg rounded-lg border border-border bg-muted/40 p-10 text-center">
				<h1 className="text-2xl font-bold text-foreground">
					{tt.title
						.replace("{inviterName}", inviterName)
						.replace("{propertyName}", property.name)
						.replace("{role}", t.properties.share.roles[inv.role])}
				</h1>

				{!user ? (
					<div className="mt-8">
						<Link
							to={`/auth?next=${encodeURIComponent(`/invitations/${token}`)}`}
						>
							<Button variant="brand" size="lg">
								{tt.signInPrompt.replace("{invitationEmail}", invitationEmail)}
							</Button>
						</Link>
					</div>
				) : emailMatches ? (
					<div className="mt-8">
						<Button
							variant="brand"
							size="lg"
							onClick={() => acceptMutation.mutate()}
							disabled={acceptMutation.isPending}
						>
							{acceptMutation.isPending ? tt.accepting : tt.accept}
						</Button>
					</div>
				) : (
					<div className="mt-8">
						<p className="text-sm text-destructive mb-4">
							{tt.emailMismatch.replace("{invitationEmail}", invitationEmail)}
						</p>
						<Button variant="ghost" onClick={() => void logout()}>
							{t.sidebar.signOut}
						</Button>
					</div>
				)}
			</div>
		</div>
	)
}
